/**
 * LabBloodTestsPage (Phase-2, Module 8 — lab admin side)
 *
 * Lab admin confirms bookings, marks sample collected / processing, uploads the
 * report file, and completes bookings.
 *
 * @author Civilhealth Development Team
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Box, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Stack, TextField, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { WaterDrop as BloodIcon, Upload as UploadIcon } from '@mui/icons-material';
import { PageScaffold, StatusBadge, FileUpload } from '../../components/common';
import { BloodTestApi, toAbsoluteFileUrl } from '../../services/phase2Api';
import { COLLECTION_TYPES, labelFor } from '../../utils/phase2Constants';
import type { BloodTestBooking } from '../../types/phase2';

const LabBloodTestsPage: React.FC = () => {
  const [rows, setRows] = useState<BloodTestBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('BOOKED');
  const [uploadId, setUploadId] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params: Record<string, unknown> = { page: 0, size: 100 };
      if (statusFilter) params.status = statusFilter;
      const page = await BloodTestApi.listAll(params);
      setRows(page?.content || []);
    } catch (e: any) { setError(e?.response?.data?.message || 'Failed to load bookings'); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<BloodTestBooking>, label: string) => {
    try { await fn(); load(); } catch (e: any) { setError(e?.response?.data?.message || `${label} failed`); }
  };

  const handleUpload = async () => {
    if (uploadId == null || !file) return;
    setUploading(true); setError(null);
    try { await BloodTestApi.uploadReport(uploadId, file, notes || undefined); setUploadId(null); setFile(null); setNotes(''); load(); }
    catch (e: any) { setError(e?.response?.data?.message || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const columns: GridColDef[] = [
    { field: 'bookingNumber', headerName: 'Booking No.', width: 140 },
    { field: 'officerName', headerName: 'Officer', width: 150, renderCell: (p) => p.value || '—' },
    { field: 'bloodTestName', headerName: 'Test', flex: 1.2, minWidth: 150 },
    { field: 'bookingDate', headerName: 'Date', width: 120 },
    { field: 'collectionType', headerName: 'Collection', width: 150, renderCell: (p) => <Chip size="small" label={labelFor(COLLECTION_TYPES, p.value)} variant="outlined" /> },
    { field: 'status', headerName: 'Status', width: 140, renderCell: (p) => <StatusBadge status={p.value} size="small" /> },
    {
      field: 'actions', headerName: 'Actions', width: 360, sortable: false,
      renderCell: (p) => {
        const s = p.row.status as string;
        const id = p.row.id as number;
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
            {s === 'BOOKED' && <Button size="small" variant="contained" color="primary" onClick={() => act(() => BloodTestApi.confirm(id), 'Confirm')} sx={{ textTransform: 'none', mr: 0.5 }}>Confirm</Button>}
            {s === 'CONFIRMED' && <Button size="small" variant="contained" color="warning" onClick={() => act(() => BloodTestApi.markSampleCollected(id), 'Sample Collected')} sx={{ textTransform: 'none', mr: 0.5 }}>Sample Collected</Button>}
            {(s === 'CONFIRMED' || s === 'SAMPLE_COLLECTED') && <Button size="small" variant="contained" color="info" onClick={() => act(() => BloodTestApi.markProcessing(id), 'Processing')} sx={{ textTransform: 'none', mr: 0.5 }}>Processing</Button>}
            {(s === 'SAMPLE_COLLECTED' || s === 'PROCESSING') && <Button size="small" variant="contained" color="primary" startIcon={<UploadIcon />} onClick={() => setUploadId(id)} sx={{ textTransform: 'none', mr: 0.5 }}>Upload Report</Button>}
            {s === 'REPORT_READY' && <Button size="small" variant="contained" color="success" onClick={() => act(() => BloodTestApi.complete(id), 'Complete')} sx={{ textTransform: 'none', mr: 0.5 }}>Complete</Button>}
            {p.row.reportUrl && <Button size="small" href={toAbsoluteFileUrl(p.row.reportUrl)} target="_blank" rel="noopener noreferrer" sx={{ textTransform: 'none' }}>View</Button>}
          </Box>
        );
      },
    },
  ];

  return (
    <PageScaffold title="Lab Blood Tests" subtitle="Process bookings & upload reports" icon={<BloodIcon />}
      loading={loading && rows.length === 0} error={error} onDismissError={() => setError(null)}>
      <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
        <InputLabel>Status</InputLabel>
        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="BOOKED">Booked</MenuItem>
          <MenuItem value="CONFIRMED">Confirmed</MenuItem>
          <MenuItem value="SAMPLE_COLLECTED">Sample Collected</MenuItem>
          <MenuItem value="PROCESSING">Processing</MenuItem>
          <MenuItem value="REPORT_READY">Report Ready</MenuItem>
          <MenuItem value="COMPLETED">Completed</MenuItem>
        </Select>
      </FormControl>
      <Box sx={{ height: 540, bgcolor: '#fff', borderRadius: 3, border: '1px solid #E5E7EB' }}>
        <DataGrid rows={rows} columns={columns} getRowId={(r) => r.id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
      </Box>

      <Dialog open={uploadId != null} onClose={() => !uploading && setUploadId(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Upload Blood Test Report</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Notes (optional)" multiline minRows={2} value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth />
            <FileUpload onFileSelected={setFile} uploading={uploading} label="Select report file (PDF / image)" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUploadId(null)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!file || uploading} sx={{ textTransform: 'none', fontWeight: 600 }}>{uploading ? 'Uploading...' : 'Upload'}</Button>
        </DialogActions>
      </Dialog>
    </PageScaffold>
  );
};

export default LabBloodTestsPage;